import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import { roleLabel } from "@/lib/roles";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { ArrowLeft, CheckCircle2, ClipboardCheck, ListChecks, Truck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";

const INR = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);
const STATUSES = ["planning", "in_production", "qa", "delivered", "on_hold", "completed", "cancelled"];
const DEFAULT_RP_LINES = [{ role: "Designer", discipline: "architectural", software: [], hours: 40, weeks: 4 }];
let _rowKey = 0;
const withKey = (o) => ({ ...o, _k: o._k ?? `k${++_rowKey}` });

export default function ProjectDetail() {
  const { id } = useParams();
  const qc = useQueryClient();
  const { user } = useAuth();
  const [hourForm, setHourForm] = useState({ date: new Date().toISOString().slice(0, 10), hours: 4, notes: "" });

  const { data, isLoading } = useQuery({ queryKey: ["project", id], queryFn: async () => (await api.get(`/projects/${id}`)).data });

  const logMut = useMutation({
    mutationFn: (p) => api.post(`/projects/${id}/production-hours`, { ...p, project_id: id }),
    onSuccess: () => {
      toast.success("Hours logged");
      qc.invalidateQueries({ queryKey: ["project", id] });
      qc.invalidateQueries({ queryKey: ["kpis"] });
      qc.invalidateQueries({ queryKey: ["hours-trend"] });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const approveMut = useMutation({
    mutationFn: (hid) => api.post(`/projects/${id}/production-hours/${hid}/approve`),
    onSuccess: () => {
      toast.success("Hour approved");
      qc.invalidateQueries({ queryKey: ["project", id] });
      qc.invalidateQueries({ queryKey: ["kpis"] });
    },
  });
  const statusMut = useMutation({
    mutationFn: (status) => api.patch(`/projects/${id}`, { status }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["project", id] }); qc.invalidateQueries({ queryKey: ["projects"] }); toast.success("Project updated"); },
  });

  if (isLoading || !data) return <div className="p-12 text-sm text-zinc-500 font-mono">Loading project…</div>;
  const { project, client, quotation, hours, team, total_hours, approved_hours } = data;

  const canApprove = ["managing_director", "operations_manager", "team_lead"].includes(user?.role);

  return (
    <div data-testid="project-detail">
      <PageHeader
        eyebrow={<Link to="/app/projects" className="hover:underline inline-flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> All Projects</Link>}
        title={project.name}
        description={
          <span className="font-mono text-xs text-zinc-500">
            {project.code} · <Link to={`/app/clients/${client.id}`} className="underline underline-offset-2">{client.name}</Link>
          </span>
        }
        actions={
          <div className="flex items-center gap-2">
            <Select value={project.status} onValueChange={(v) => statusMut.mutate(v)}>
              <SelectTrigger className="h-9 w-40" data-testid="prj-detail-status"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace("_", " ")}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        }
      />
      <div className="max-w-[1400px] mx-auto px-8 py-6">
        <div className="grid md:grid-cols-4 gap-4 mb-6">
          <div className="border border-zinc-200 rounded-lg bg-white p-4">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Billing</div>
            <div className="mt-1 font-heading text-lg font-bold capitalize">{project.billing_model.replace("_", " ")}</div>
          </div>
          <div className="border border-zinc-200 rounded-lg bg-white p-4">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Estimated Hours</div>
            <div className="mt-1 font-heading text-lg font-bold">{project.estimated_hours}</div>
          </div>
          <div className="border border-zinc-200 rounded-lg bg-white p-4">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Logged Hours</div>
            <div className="mt-1 font-heading text-lg font-bold">{total_hours}<span className="text-zinc-400 text-sm font-normal"> · {approved_hours} approved</span></div>
          </div>
          <div className="border border-zinc-200 rounded-lg bg-white p-4">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Budget</div>
            <div className="mt-1 font-heading text-lg font-bold">{INR(project.budget)}</div>
          </div>
        </div>

        <Tabs defaultValue="hours">
          <TabsList data-testid="prj-tabs">
            <TabsTrigger value="hours" data-testid="prj-tab-hours">Production Hours ({hours.length})</TabsTrigger>
            <TabsTrigger value="team" data-testid="prj-tab-team">Team ({team.length})</TabsTrigger>
            <TabsTrigger value="quotation" data-testid="prj-tab-quo">Quotation</TabsTrigger>
            <TabsTrigger value="resource" data-testid="prj-tab-resource">Resource Plan</TabsTrigger>
            <TabsTrigger value="qa" data-testid="prj-tab-qa">QA</TabsTrigger>
            <TabsTrigger value="deliveries" data-testid="prj-tab-deliveries">Deliveries</TabsTrigger>
            <TabsTrigger value="overview" data-testid="prj-tab-overview">Overview</TabsTrigger>
          </TabsList>

          <TabsContent value="hours" className="mt-4 space-y-4">
            <form onSubmit={(e) => { e.preventDefault(); logMut.mutate({ ...hourForm, hours: Number(hourForm.hours) }); }} className="border border-zinc-200 rounded-lg bg-white p-4 flex flex-wrap items-end gap-3" data-testid="log-hours-form">
              <div><Label htmlFor="h-date">Date</Label><Input id="h-date" type="date" value={hourForm.date} onChange={(e) => setHourForm({ ...hourForm, date: e.target.value })} data-testid="hours-date" /></div>
              <div><Label htmlFor="h-hours">Hours</Label><Input id="h-hours" type="number" step="0.25" value={hourForm.hours} onChange={(e) => setHourForm({ ...hourForm, hours: e.target.value })} data-testid="hours-value" /></div>
              <div className="flex-1 min-w-[200px]"><Label htmlFor="h-notes">Notes</Label><Input id="h-notes" value={hourForm.notes} onChange={(e) => setHourForm({ ...hourForm, notes: e.target.value })} data-testid="hours-notes" /></div>
              <Button type="submit" disabled={logMut.isPending} data-testid="hours-submit">Log hours</Button>
            </form>

            <div className="border border-zinc-200 rounded-lg bg-white overflow-hidden" data-testid="hours-table">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 border-b border-zinc-200">
                  <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500">
                    <th className="px-4 py-3">Date</th><th className="px-4 py-3 text-right">Hours</th><th className="px-4 py-3">Notes</th><th className="px-4 py-3">Approved</th><th></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {hours.map((h) => (
                    <tr key={h.id} data-testid={`hour-row-${h.id}`}>
                      <td className="px-4 py-3 font-mono text-xs">{h.date}</td>
                      <td className="px-4 py-3 text-right font-mono">{h.hours}</td>
                      <td className="px-4 py-3 text-zinc-600">{h.notes || "—"}</td>
                      <td className="px-4 py-3">{h.approved ? <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Approved</Badge> : <Badge variant="outline">Pending</Badge>}</td>
                      <td className="px-4 py-3 text-right">
                        {!h.approved && canApprove && <button onClick={() => approveMut.mutate(h.id)} className="text-xs text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1" data-testid={`hour-approve-${h.id}`}><CheckCircle2 className="w-3 h-3" /> Approve</button>}
                      </td>
                    </tr>
                  ))}
                  {hours.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-zinc-500">No hours logged yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="team" className="mt-4">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {team.map((m) => (
                <div key={m.user_id} className="border border-zinc-200 rounded-lg bg-white p-4" data-testid={`team-${m.user_id}`}>
                  <div className="font-medium">{m.name}</div>
                  <div className="text-[11px] text-zinc-500">{m.designation || roleLabel(m.role)}</div>
                  <div className="text-xs text-zinc-500 mt-1 font-mono">{m.email}</div>
                </div>
              ))}
              {team.length === 0 && <div className="text-sm text-zinc-500">No team assigned. Edit the project to add team members.</div>}
            </div>
          </TabsContent>

          <TabsContent value="quotation" className="mt-4">
            {quotation ? (
              <div className="border border-zinc-200 rounded-lg bg-white p-6" data-testid="quo-panel">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-mono text-xs text-zinc-500">{quotation.number}</div>
                    <div className="font-heading text-xl font-bold mt-1">{quotation.title}</div>
                  </div>
                  <Badge variant="outline" className="capitalize">{quotation.status}</Badge>
                </div>
                <div className="mt-6 space-y-2">
                  {quotation.items.map((it, i) => (
                    <div key={`${it.description || "line"}-${i}`} className="flex justify-between text-sm border-b border-zinc-100 pb-2">
                      <div>{it.description} · <span className="text-zinc-500 font-mono text-xs">{it.quantity} {it.unit} × ₹{it.rate}</span></div>
                      <div className="font-mono">{INR(it.quantity * it.rate)}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 ml-auto max-w-xs space-y-1 text-sm">
                  <div className="flex justify-between text-zinc-500"><span>Subtotal</span><span className="font-mono">{INR(quotation.subtotal)}</span></div>
                  <div className="flex justify-between text-zinc-500"><span>Tax ({quotation.tax_percent}%)</span><span className="font-mono">{INR(quotation.tax_amount)}</span></div>
                  <div className="flex justify-between border-t border-zinc-200 pt-1 font-bold"><span>Total</span><span className="font-mono">{INR(quotation.total)}</span></div>
                </div>
              </div>
            ) : (
              <div className="text-sm text-zinc-500">No linked quotation.</div>
            )}
          </TabsContent>

          <TabsContent value="resource" className="mt-4">
            <ResourcePlanTab pid={id} />
          </TabsContent>

          <TabsContent value="qa" className="mt-4">
            <QATab pid={id} />
          </TabsContent>

          <TabsContent value="deliveries" className="mt-4">
            <DeliveriesTab pid={id} />
          </TabsContent>

          <TabsContent value="overview" className="mt-4">
            <div className="border border-zinc-200 rounded-lg bg-white p-6 text-sm text-zinc-700 leading-relaxed" data-testid="prj-overview">
              {project.description || "No description on file."}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function ResourcePlanTab({ pid }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["rp", pid], queryFn: async () => (await api.get(`/projects/${pid}/resource-plan`)).data });
  const rp = data?.resource_plan;
  const [lines, setLines] = useState(() => (rp?.lines?.length ? rp.lines.map(withKey) : DEFAULT_RP_LINES.map(withKey)));
  const [buffer, setBuffer] = useState(rp?.buffer_pct ?? 10);
  useEffect(() => {
    if (rp) {
      setLines(rp.lines?.length ? rp.lines.map(withKey) : DEFAULT_RP_LINES.map(withKey));
      setBuffer(rp.buffer_pct ?? 10);
    }
    // Resync only when the RP identity changes; the lines/buffer inside are user-editable
    // between syncs and must not be overwritten on every rerender.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rp?.id, rp?.updated_at]);
  const saveMut = useMutation({
    mutationFn: (body) => api.put(`/projects/${pid}/resource-plan`, body),
    onSuccess: () => { toast.success("Resource plan saved"); qc.invalidateQueries({ queryKey: ["rp", pid] }); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const approveMut = useMutation({
    mutationFn: () => api.post(`/projects/${pid}/resource-plan/approve`),
    onSuccess: () => { toast.success("Resource plan approved"); qc.invalidateQueries({ queryKey: ["rp", pid] }); },
  });
  const total = lines.reduce((s, l) => s + (Number(l.hours) || 0), 0);
  const isApproved = rp?.status === "approved";
  return (
    <div className="border border-zinc-200 rounded-lg bg-white p-6" data-testid="rp-panel">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Resource Plan</div>
          <div className="font-heading text-lg font-bold">Status: <span className="capitalize">{rp?.status || "not started"}</span> · Total {total}h + {buffer}% buffer</div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setLines([...lines, withKey({ role: "Designer", discipline: "architectural", software: [], hours: 20, weeks: 2 })])} disabled={isApproved} data-testid="rp-add"><Plus className="w-3 h-3 mr-1" />Add row</Button>
          <Button size="sm" disabled={isApproved || saveMut.isPending} onClick={() => saveMut.mutate({ lines: lines.map((l) => { const { _k, ...rest } = l; return { ...rest, hours: Number(l.hours) || 0, weeks: Number(l.weeks) || 1 }; }), buffer_pct: Number(buffer) || 0 })} data-testid="rp-save">Save</Button>
          {rp && !isApproved && <Button size="sm" onClick={() => approveMut.mutate()} data-testid="rp-approve" className="bg-emerald-600 hover:bg-emerald-700"><CheckCircle2 className="w-3 h-3 mr-1" />Approve</Button>}
        </div>
      </div>
      <table className="w-full text-sm">
        <thead className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-100">
          <tr><th className="text-left py-2">Role</th><th className="text-left py-2">Discipline</th><th className="text-right py-2 w-24">Hours</th><th className="text-right py-2 w-20">Weeks</th><th></th></tr>
        </thead>
        <tbody className="divide-y divide-zinc-100">
          {lines.map((l, i) => (
            <tr key={l._k}>
              <td className="py-2 pr-2"><Input value={l.role} onChange={(e) => { const n = [...lines]; n[i] = { ...l, role: e.target.value }; setLines(n); }} disabled={isApproved} className="h-8" data-testid={`rp-role-${i}`} /></td>
              <td className="py-2 pr-2"><Input value={l.discipline} onChange={(e) => { const n = [...lines]; n[i] = { ...l, discipline: e.target.value }; setLines(n); }} disabled={isApproved} className="h-8" data-testid={`rp-disc-${i}`} /></td>
              <td className="py-2 pr-2"><Input type="number" className="h-8 text-right" value={l.hours} onChange={(e) => { const n = [...lines]; n[i] = { ...l, hours: e.target.value }; setLines(n); }} disabled={isApproved} data-testid={`rp-hrs-${i}`} /></td>
              <td className="py-2 pr-2"><Input type="number" className="h-8 text-right" value={l.weeks} onChange={(e) => { const n = [...lines]; n[i] = { ...l, weeks: e.target.value }; setLines(n); }} disabled={isApproved} data-testid={`rp-wks-${i}`} /></td>
              <td className="py-2"><button type="button" onClick={() => setLines(lines.filter((_, j) => j !== i))} disabled={isApproved} className="text-zinc-400 hover:text-red-600 disabled:opacity-30" data-testid={`rp-del-${i}`}>×</button></td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-4 flex items-center gap-3">
        <Label htmlFor="rp-buf" className="text-xs">Buffer %</Label>
        <Input id="rp-buf" type="number" value={buffer} onChange={(e) => setBuffer(e.target.value)} disabled={isApproved} className="h-8 w-24" data-testid="rp-buffer" />
      </div>
      {isApproved && <div className="mt-3 text-xs text-emerald-700">✓ Approved — Team Assignment now unlocked on the Team tab.</div>}
    </div>
  );
}

function QATab({ pid }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["qa", pid], queryFn: async () => (await api.get(`/projects/${pid}/qa`)).data });
  const [checklist, setChecklist] = useState(data?.checklist || []);
  const [signed, setSigned] = useState(data?.signed_off || false);
  const [notes, setNotes] = useState(data?.notes || "");
  useEffect(() => {
    if (data) {
      setChecklist(data.checklist || []);
      setSigned(!!data.signed_off);
      setNotes(data.notes || "");
    }
  }, [data]);
  const saveMut = useMutation({
    mutationFn: (body) => api.put(`/projects/${pid}/qa`, body),
    onSuccess: () => { toast.success(signed ? "QA signed off" : "QA saved"); qc.invalidateQueries({ queryKey: ["qa", pid] }); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const doneCount = checklist.filter((c) => c.checked).length;
  return (
    <div className="border border-zinc-200 rounded-lg bg-white p-6" data-testid="qa-panel">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Quality Assurance</div>
          <div className="font-heading text-lg font-bold">{doneCount} / {checklist.length} items complete{signed && <span className="ml-2 text-emerald-700 text-sm">· signed off</span>}</div>
        </div>
        <Button size="sm" onClick={() => saveMut.mutate({ project_id: pid, checklist, signed_off: signed, notes: notes || undefined })} disabled={saveMut.isPending} data-testid="qa-save"><ClipboardCheck className="w-3 h-3 mr-1" />Save</Button>
      </div>
      <div className="space-y-2">
        {checklist.map((c, i) => (
          <label key={c.title || `qa-${i}`} className="flex items-start gap-3 p-2 rounded hover:bg-zinc-50">
            <Checkbox checked={c.checked} onCheckedChange={(v) => { const n = [...checklist]; n[i] = { ...c, checked: !!v }; setChecklist(n); }} data-testid={`qa-item-${i}`} />
            <span className={c.checked ? "text-zinc-400 line-through text-sm" : "text-sm"}>{c.title}</span>
          </label>
        ))}
      </div>
      <div className="mt-4">
        <Label htmlFor="qa-notes">Notes</Label>
        <Textarea id="qa-notes" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="qa-notes" />
      </div>
      <label className="mt-4 flex items-center gap-2 pt-3 border-t border-zinc-100">
        <Checkbox checked={signed} onCheckedChange={(v) => setSigned(!!v)} disabled={doneCount < checklist.length} data-testid="qa-signoff" />
        <span className="text-sm font-medium">Sign off QA</span>
        {doneCount < checklist.length && <span className="text-[11px] text-zinc-500">complete all items first</span>}
      </label>
    </div>
  );
}

function DeliveriesTab({ pid }) {
  const qc = useQueryClient();
  const { data: rows = [] } = useQuery({ queryKey: ["deliveries", pid], queryFn: async () => (await api.get(`/deliveries?project_id=${pid}`)).data });
  const [form, setForm] = useState({ title: "", format: "PDF", delivered_at: new Date().toISOString().slice(0, 10), external_link: "" });
  const createMut = useMutation({
    mutationFn: (p) => api.post("/deliveries", p),
    onSuccess: () => { toast.success("Delivery logged"); setForm({ title: "", format: "PDF", delivered_at: new Date().toISOString().slice(0, 10), external_link: "" }); qc.invalidateQueries({ queryKey: ["deliveries", pid] }); qc.invalidateQueries({ queryKey: ["deliveries"] }); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  return (
    <div className="space-y-4" data-testid="prj-deliveries-panel">
      <form onSubmit={(e) => { e.preventDefault(); if (!form.title) return toast.error("Title required"); createMut.mutate({ project_id: pid, ...form, external_link: form.external_link || undefined }); }} className="border border-zinc-200 rounded-lg bg-white p-4 grid grid-cols-1 md:grid-cols-5 gap-3 items-end" data-testid="prj-del-form">
        <div className="md:col-span-2"><Label htmlFor="pd-t">Title</Label><Input id="pd-t" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="prj-del-title" /></div>
        <div><Label htmlFor="pd-f">Format</Label><Input id="pd-f" value={form.format} onChange={(e) => setForm({ ...form, format: e.target.value })} data-testid="prj-del-format" /></div>
        <div><Label htmlFor="pd-d">Delivered</Label><Input id="pd-d" type="date" value={form.delivered_at} onChange={(e) => setForm({ ...form, delivered_at: e.target.value })} data-testid="prj-del-date" /></div>
        <Button type="submit" disabled={createMut.isPending} data-testid="prj-del-submit"><Truck className="w-4 h-4 mr-1.5" />Log</Button>
      </form>
      <div className="border border-zinc-200 rounded-lg bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200"><tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500"><th className="px-4 py-3">Delivered</th><th className="px-4 py-3">Title</th><th className="px-4 py-3">Format</th><th className="px-4 py-3">Link</th></tr></thead>
          <tbody className="divide-y divide-zinc-100">
            {rows.map((d) => <tr key={d.id}><td className="px-4 py-3 font-mono text-xs">{d.delivered_at}</td><td className="px-4 py-3 font-medium">{d.title}</td><td className="px-4 py-3 font-mono text-xs">{d.format || "—"}</td><td className="px-4 py-3">{d.external_link ? <a href={d.external_link} className="text-xs underline underline-offset-2" target="_blank" rel="noreferrer">Open</a> : "—"}</td></tr>)}
            {rows.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-sm text-zinc-500">No deliveries logged.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
